// tslint:disable:jsx-boolean-value
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Page from 'models/IObject';
import SearchInput from 'components/admin2/ui/SearchInput';
import useLegacyCollection from 'hooks/use-legacy-collection';
import usePagesV3Collection from 'hooks/use-pages-v3-collection';
import hash from 'json-stable-stringify';
import { useSelector } from 'react-redux';
import { getPendingPageDocs, getRefreshKey, getDraftHomeId } from 'services/admin/selectors';
import { getCurrentPageId } from 'services/app/selectors';
import { useTrackAdminNavigation } from 'hooks';
import PageResults from './PageResults';
import { usePageNavigationContext } from './Context';
import { getActiveSiteFeatures } from 'services/app/selectors/common';
import { Feature } from 'services/feature-gate';

const PAGE_LIMIT = 50;

interface PageSearchProps {
  firstPage: Page[];
}

export default function({ firstPage }: PageSearchProps) {
  const {
    searchText,
    setPageNavigationSearch: onSearch,
  } = usePageNavigationContext();
  const features = useSelector(getActiveSiteFeatures);
  const pagesHook = features[Feature.PAGES_V3] ? usePagesV3Collection : useLegacyCollection;
  const homeId = useSelector(getDraftHomeId);
  const currentPageId = useSelector(getCurrentPageId);
  const pageDraftDocs = useSelector(getPendingPageDocs);
  const refreshKey = useSelector(getRefreshKey);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageList, setPageList] = useState<Page[][]>([firstPage]);
  const trackAdminNavigation = useTrackAdminNavigation();
  const [pageResults] = pagesHook<Page>({
    collection: 'pages',
    limit: PAGE_LIMIT,
    pageOffset: pageIndex,
    refreshKey,
    search: '',
  });
  const [searchResults] = pagesHook<Page>({
    collection: 'pages',
    limit: searchText ? PAGE_LIMIT : 0,
    pageOffset: 0,
    refreshKey,
    search: searchText,
  });

  useEffect(() => {
    if (!searchText && pageResults?.length) {
      setPageList(pages => {
        const updatedPages = [...pages];
        updatedPages[pageIndex] = pageResults;
        return updatedPages;
      });
    }
  }, [hash(pageResults)]);

  const filterDrafts = (results: Record<string, Page>) => {
    if (!searchText) {
      return results;
    }
    const lowerCaseText = searchText.toLowerCase();
    return Object.entries(results).reduce((filtered, [key, result]) => {
      const name = result?.data?.name?.toLowerCase();
      if (name && name.includes(lowerCaseText)) {
        filtered[key] = result;
      }
      return filtered;
    }, {} as typeof results);
  };

  const handleTrackSearchClick = () => {
    trackAdminNavigation({
      menu: 'channel_select',
      element: 'search',
    });
  };

  const nextPage = () => {
    if (!searchText) {
      setPageIndex(pageList.length);
    }
  };

  return (
    <PageSearch>
      <SearchInput
        height={25}
        onSearch={onSearch}
        onClick={handleTrackSearchClick}
        padding="12px 0"
        searchPlaceholderKey="ADMIN_PLACEHOLDER_SEARCH"
        short
        testIdSearchInput="channelSearchInput"
      />
      <PageResults
        currentPageId={currentPageId}
        draftDocs={filterDrafts(pageDraftDocs)}
        homeId={homeId}
        onLoadMore={nextPage}
        pages={searchText ? searchResults : pageList.flat()}
      />
    </PageSearch>
  );
}

const PageSearch = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
`;
